import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';

import type {
  FetchProfessionalSort,
  FetchProfessionalsParams,
  ProfessionalsResponse,
} from './types';

/**
 * Fetches professionals for a specific shop
 * @param params - The shop ID, include options, and language
 * @returns Promise<ProfessionalsResponse> - Array of professionals
 */
const fetchProfessionalsWithLanguage = async ({
  shopId,
  include = ['photos', 'days_off', 'days_on', 'schedules'],
  sortBy = [],
}: FetchProfessionalsParams): Promise<ProfessionalsResponse> => {
  if (!shopId) {
    throw new Error('Shop ID is required to fetch professionals');
  }

  const params: Record<string, string> = {};
  if (include.length > 0) {
    params.include = include.join(',');
  }

  if (sortBy.length > 0) {
    params.sortBy = sortBy.map((i) => `${i.name}:${i.type}`).join(',');
  }

  const { data } = await apiClient.get<ProfessionalsResponse>(
    `/v1/shop/${shopId}/professional`,
    { params },
  );

  if (!Array.isArray(data)) {
    throw new Error('Invalid response format: expected array of professionals');
  }

  return data.sort((a, b) => a.barber.order - b.barber.order);
};

export const getProfessionalsQuery = (
  shopId: string,
  include: string[] = ['photos', 'days_off', 'days_on', 'schedules'],
  enabled: boolean = true,
  sortBy: FetchProfessionalSort[] = [
    { name: 'order', type: 'asc' },
    { name: 'createdAt', type: 'desc' },
  ],
) =>
  queryOptions<ProfessionalsResponse>({
    queryKey: ['booking', 'professionals', shopId, { include }, sortBy],
    queryFn: () => fetchProfessionalsWithLanguage({ shopId, include, sortBy }),
    enabled: !!shopId && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
